import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ClockIcon } from '@pkg/assets/icons/v2/clock.svg';
import { YatPill } from '@pkg/components';
import { useAppDispatch, useAppSelector, useFormattedTime } from '@pkg/hooks';

import { apiFactory } from '@app/client';
import { Cart } from '@app/client/resources';
import { YAT_PICKER } from '@app/routes/paths';
import { setReduxCartItems } from '@app/store/actions/picker';
import { addAlert } from '@app/store/slices';

import './CheckoutCountdown.scss';

export const YatCheckoutCountdown = () => {
    const { t } = useTranslation('yat_picker');
    const { secondsRemaining } = useAppSelector((state) => state.picker);

    const start = useRef(0);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [counterInterval, setCounterInterval] = useState(0);
    const [internalTime, setInternalTime] = useState<number>();

    const formattedTime = useFormattedTime(internalTime ?? secondsRemaining ?? 0);

    useEffect(() => {
        return () => {
            clearTimeout(counterInterval);
        };
    }, [counterInterval]);

    const handleExpireCart = useCallback(async () => {
        const res = await apiFactory().call(Cart.convertToDraft, {});
        if (res.ok) {
            dispatch(setReduxCartItems(res.value));
        }
        navigate(YAT_PICKER, {
            state: { afterDraftConvert: true, enableGoBack: true },
        });
    }, [navigate, dispatch]);

    const handleTimeOut = useCallback(
        (timestamp: number) => {
            if (!secondsRemaining) {
                return;
            }
            if (start.current === 0) {
                start.current = timestamp;
            }
            const elapsed = timestamp - start.current;
            if (elapsed < secondsRemaining * 1000) {
                setInternalTime(Math.floor((secondsRemaining * 1000 - elapsed) / 1000));
                setCounterInterval(
                    window.setTimeout(() => {
                        handleTimeOut(new Date().getTime());
                    }, 1000),
                );
            } else {
                dispatch(addAlert({ message: t('checkout_redirect'), timeout: 5000 }));
                handleExpireCart();
            }
        },
        [secondsRemaining, handleExpireCart, t, dispatch],
    );

    useEffect(() => {
        if (secondsRemaining !== null && secondsRemaining !== undefined) {
            setInternalTime(secondsRemaining);
            handleTimeOut(new Date().getTime());
        }
    }, [secondsRemaining, handleTimeOut]);

    const formattedTimeMarkup = useMemo(() => {
        if (internalTime === undefined || secondsRemaining === undefined || secondsRemaining === null) {
            return '--:--';
        }
        return formattedTime;
    }, [formattedTime, internalTime, secondsRemaining]);

    const warnUser = useMemo(() => {
        return typeof internalTime === 'number' && internalTime <= 60;
    }, [internalTime]);

    return (
        <YatPill
            size="large"
            variant={warnUser ? 'danger' : null}
            classes={`yat-checkout-countdown ${warnUser && 'yat-checkout-countdown--pulse'}`}
        >
            <ClockIcon className="yat-checkout-countdown__icon" />
            <span>{formattedTimeMarkup}</span>
        </YatPill>
    );
};
